import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import useStore from "../../store/useStore";
import api from "../../services/api";
import { resolveAssetUrl } from "../../utils/assetUrl";
import buildInfo from "../../buildInfo.json";
import gamrentLogo from "../../assets/gamrent-logo.jpg";

const normalizeRole = (role) => String(role || "").toLowerCase().replace(/[^a-z]/g, "");

const Header = ({ toggleSidebar, onShowGuide }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, authRole, logout } = useStore();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [notificationLoading, setNotificationLoading] = useState(false);
  const [notificationUnreadCount, setNotificationUnreadCount] = useState(0);
  const [notificationItems, setNotificationItems] = useState([]);
  const [notificationError, setNotificationError] = useState("");
  const [notificationBadgeVisible, setNotificationBadgeVisible] = useState(false);
  const notificationUnreadCountRef = useRef(0);
  const [companyName, setCompanyName] = useState("");
  const [companyLogo, setCompanyLogo] = useState("");
  const [superAdminSession, setSuperAdminSession] = useState(
    () => sessionStorage.getItem('gamrent-superadmin-session') === '1'
  );

  const normalizedRole = normalizeRole(authRole || user?.role);
  const isSuperAdmin = normalizedRole === "superadmin";
  const isSuperAdminView = isSuperAdmin || (!normalizedRole && superAdminSession);
  const superAdminDisplayName = "Gamrent Admin Platform";

  useEffect(() => {
    if (!user) {
      setSuperAdminSession(false);
      sessionStorage.removeItem('gamrent-superadmin-session');
      return;
    }

    if (isSuperAdmin) {
      setSuperAdminSession(true);
      sessionStorage.setItem('gamrent-superadmin-session', '1');
    }
  }, [user, isSuperAdmin]);

  useEffect(() => {
    let isCancelled = false;

    const applyStoredCompany = () => {
      const storeCompanyName = (user?.companyName || user?.company || "").trim();
      const storeCompanyLogo = (user?.companyLogo || user?.logo || "").trim();

      if (storeCompanyName || storeCompanyLogo) {
        setCompanyName(storeCompanyName || "Property Management");
        setCompanyLogo(storeCompanyLogo);
      } else {
        setCompanyName("Property Management");
        setCompanyLogo("");
      }
    };

    const fetchCompanyName = async () => {
      try {
        const res = await api.get('/companies/my-company');
        if (isCancelled) return;

        if (res.data.success && res.data.data) {
          setCompanyName(res.data.data.companyName || 'Property Management');
          setCompanyLogo(res.data.data.logo || '');
        }
      } catch (error) {
        if (isCancelled) return;

        console.error('Error fetching company:', error);
        applyStoredCompany();
      }
    };

    if (!user) {
      setCompanyName("");
      setCompanyLogo("");
      return () => {
        isCancelled = true;
      };
    }

    if (isSuperAdminView) {
      setCompanyName(superAdminDisplayName || "Super Admin");
      setCompanyLogo("");
      return () => {
        isCancelled = true;
      };
    }

    applyStoredCompany();
    fetchCompanyName();

    return () => {
      isCancelled = true;
    };
  }, [
    user,
    isSuperAdminView,
    superAdminDisplayName,
    location.pathname,
    user?.companyName,
    user?.companyLogo,
    user?.company,
    user?.logo
  ]);

  const headerTitle = isSuperAdminView ? superAdminDisplayName : companyName;
  const headerLogoUrl = isSuperAdminView ? "" : resolveAssetUrl(companyLogo);

  const fetchNotificationSummary = useCallback(async () => {
    try {
      setNotificationLoading(true);
      setNotificationError("");

      const res = await api.get('/notifications/summary');
      const summary = res.data?.data || {};
      const nextUnreadCount = Number(summary.unreadCount || 0);
      const previousUnreadCount = notificationUnreadCountRef.current;

      notificationUnreadCountRef.current = nextUnreadCount;

      setNotificationItems(Array.isArray(summary.notifications) ? summary.notifications : []);
      setNotificationUnreadCount(nextUnreadCount);

      if (nextUnreadCount === 0) {
        setNotificationBadgeVisible(false);
      } else if (nextUnreadCount !== previousUnreadCount) {
        setNotificationBadgeVisible(true);
      }
    } catch (error) {
      console.error('Error fetching notification summary:', error);
      setNotificationItems([]);
      setNotificationUnreadCount(0);
      setNotificationError('Notifications could not be loaded right now.');
      notificationUnreadCountRef.current = 0;
    } finally {
      setNotificationLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user) {
      setNotificationOpen(false);
      setNotificationItems([]);
      setNotificationUnreadCount(0);
      setNotificationError("");
      setNotificationBadgeVisible(false);
      notificationUnreadCountRef.current = 0;
      return;
    }

    fetchNotificationSummary();
  }, [user, fetchNotificationSummary]);

  useEffect(() => {
    if (notificationOpen) {
      setNotificationBadgeVisible(false);
      fetchNotificationSummary();
    }
  }, [notificationOpen, fetchNotificationSummary]);

  const handleNotificationToggle = () => {
    setDropdownOpen(false);
    setNotificationOpen((prev) => {
      const nextOpen = !prev;

      if (nextOpen) {
        setNotificationBadgeVisible(false);
      }

      return nextOpen;
    });
  };

  const handleOpenNotificationCenter = () => {
    setNotificationOpen(false);
    navigate('/notifications');
  };

  const handleNotificationSelect = async (item) => {
    try {
      await api.put(`/notifications/${item.id}/read`);
      setNotificationItems((prev) => prev.map((entry) => (
        entry.id === item.id ? { ...entry, isRead: 1 } : entry
      )));
      setNotificationUnreadCount((prev) => Math.max(prev - 1, 0));
    } catch (error) {
      console.error('Mark notification error:', error);
    } finally {
      setNotificationOpen(false);
      navigate('/notifications');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleProfile = () => {
    setDropdownOpen(false);
    setNotificationOpen(false);
    navigate('/profile');
  };

  return (
    <header className="sticky top-0 z-30 border-b border-teal-100 shadow-sm pt-[env(safe-area-inset-top)]">
      {/* Blur lives on its own layer: backdrop-filter on the header would trap the fixed-position overlays below */}
      <div className="absolute inset-0 -z-10 bg-white/85 backdrop-blur-md" aria-hidden="true" />
      <div className="flex items-center justify-between gap-2 px-3 py-2.5 sm:px-6 sm:py-3 lg:px-8">
        {/* Left Section */}
        <div className="flex min-w-0 items-center gap-2 sm:gap-4">
          {/* Mobile Menu Toggle */}
          <button
            onClick={toggleSidebar}
            aria-label="Open menu"
            className="lg:hidden shrink-0 p-2.5 -ml-1 hover:bg-teal-50 rounded-xl transition-colors"
          >
            <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>

          {/* Company/System Name */}
          <div className="flex min-w-0 items-center gap-2.5 sm:gap-3">
            {headerLogoUrl ? (
              <img
                src={headerLogoUrl}
                alt="Company logo"
                className="w-9 h-9 sm:w-10 sm:h-10 md:w-12 md:h-12 shrink-0 rounded-lg border border-gray-200 object-contain bg-white"
              />
            ) : (
              <img
                src={gamrentLogo}
                alt="Gamrent"
                className="lg:hidden w-9 h-9 sm:w-10 sm:h-10 shrink-0 rounded-xl shadow-sm object-cover"
              />
            )}

            <div className="min-w-0">
              {headerTitle && (
                <h1 className="system-title truncate text-base sm:text-xl md:text-2xl font-semibold text-gray-900 leading-tight">
                  {headerTitle}
                </h1>
              )}
              <p className="hidden sm:block truncate text-xs md:text-sm text-gray-600 font-medium">Gamrent | Property Management System</p>
            </div>
          </div>
        </div>

        {/* Right Section */}
        <div className="flex shrink-0 items-center gap-1.5 sm:gap-4">
          {/* Notifications */}
          <div className="relative">
            <button
              onClick={handleNotificationToggle}
              aria-expanded={notificationOpen}
              aria-label="Open notifications"
              className="relative inline-flex h-10 w-10 sm:h-11 sm:w-11 items-center justify-center rounded-2xl border border-teal-100 bg-gradient-to-br from-teal-50 to-white text-teal-700 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-teal-500/40"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>

              {notificationBadgeVisible && notificationUnreadCount > 0 && (
                <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-rose-500 shadow-sm ring-2 ring-white" />
              )}
            </button>

            {notificationOpen && (
              <>
                <div
                  className="fixed inset-0 z-20"
                  onClick={() => setNotificationOpen(false)}
                />

                <div className="fixed inset-x-3 top-[calc(env(safe-area-inset-top)+4rem)] sm:absolute sm:inset-x-auto sm:top-auto sm:right-0 sm:mt-3 sm:w-[22rem] overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_24px_64px_rgba(15,23,42,0.18)] z-30">
                  <div className="flex items-center justify-between border-b border-slate-100 bg-gradient-to-r from-slate-50 to-teal-50 px-4 py-3">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">Notifications</p>
                      <p className="text-xs text-slate-500">Latest updates from your system</p>
                    </div>
                    <button
                      onClick={handleOpenNotificationCenter}
                      className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-teal-700 shadow-sm ring-1 ring-teal-100 transition hover:bg-teal-50"
                    >
                      View all
                    </button>
                  </div>

                  <div className="max-h-[min(24rem,calc(100dvh-10rem))] overflow-auto overscroll-contain">
                    {notificationLoading ? (
                      <div className="px-4 py-6 text-sm text-slate-500">Loading notifications...</div>
                    ) : notificationError ? (
                      <div className="px-4 py-6 text-sm text-rose-600">{notificationError}</div>
                    ) : notificationItems.length === 0 ? (
                      <div className="px-4 py-8 text-sm text-slate-500">No notifications yet.</div>
                    ) : (
                      <div className="divide-y divide-slate-100">
                        {notificationItems.map((item) => {
                          const isUnread = Number(item.isRead) === 0;

                          return (
                            <button
                              key={item.id}
                              onClick={() => handleNotificationSelect(item)}
                              className={`flex w-full items-start gap-3 px-4 py-4 text-left transition hover:bg-teal-50/70 ${isUnread ? 'bg-teal-50/40' : 'bg-white'}`}
                            >
                              <span className={`mt-1 h-2.5 w-2.5 shrink-0 rounded-full ${isUnread ? 'bg-rose-500' : 'bg-slate-300'}`} />
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center justify-between gap-3">
                                  <p className="truncate text-sm font-semibold text-slate-900">{item.title}</p>
                                  <span className="whitespace-nowrap text-[11px] text-slate-500">
                                    {new Date(item.createdAt).toLocaleString()}
                                  </span>
                                </div>
                                <p className="mt-1 max-h-10 overflow-hidden text-sm leading-5 text-slate-600">{item.message}</p>
                                <p className="mt-2 text-xs text-slate-500">
                                  From {item.senderName || 'System'} • {item.audience}
                                </p>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* User Dropdown */}
          <div className="relative">
            <button
              onClick={() => {
                setNotificationOpen(false);
                setDropdownOpen(!dropdownOpen);
              }}
              aria-label="Open account menu"
              aria-expanded={dropdownOpen}
              className="flex items-center gap-1 sm:gap-2 p-1 sm:p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              {/* User Avatar */}
              <div className="w-10 h-10 shrink-0 bg-gradient-to-br from-teal-600 to-teal-700 rounded-full flex items-center justify-center text-white font-semibold border border-teal-100">
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </div>
              
              {/* Dropdown Arrow */}
              <svg className={`hidden sm:block w-4 h-4 text-gray-700 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {/* Dropdown Menu */}
            {dropdownOpen && (
              <>
                {/* Backdrop */}
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setDropdownOpen(false)}
                ></div>
                
                {/* Menu */}
                <div className="absolute right-0 mt-2 w-64 max-w-[calc(100vw-1.5rem)] bg-white rounded-2xl shadow-xl border border-teal-100 py-2 z-20 text-gray-900">
                  {/* User Info */}
                  <div className="px-4 py-3 border-b border-gray-200">
                    <p className="truncate text-sm font-semibold text-gray-900">
                      {user?.firstName} {user?.lastName}
                    </p>
                    <p className="truncate text-xs text-gray-500">{user?.email}</p>
                    <p className="text-xs text-teal-600 font-medium mt-1">{authRole || user?.role}</p>
                  </div>

                  {/* Profile Link */}
                  <button
                    onClick={handleProfile}
                    className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-teal-50 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    Profile Settings
                  </button>

                  <button
                    onClick={() => {
                      setDropdownOpen(false);
                      onShowGuide?.();
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-teal-50 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                    Show guide
                  </button>

                  {/* Logout */}
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    Logout
                  </button>

                  <div className="px-4 py-2 mt-1 border-t border-gray-100 text-[11px] text-gray-500">
                    Build {buildInfo.version} | {buildInfo.commit}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;